import { DataError } from "../../core/errors/DataError";
import { BaseLogo, getLogoOrgId, LogoType } from "../../core/models/entities/BaseLogo";
import { Design } from "../../core/models/entities/Design";
import { SheetSizeUnavailableLogo } from "../../core/models/entities/ErrorStatus";
import { OversizeSheetGuardHint } from "../../core/models/entities/LogoOptions";
import { Role } from "../../core/models/entities/Role";
import { SHEET_SIZE_UNAVAILABLE_ERROR_CODE } from "../../data/models/mappers/mapError";
import { enviroment } from "../../enviroment";

// Oversize sheet guard (flag REACT_APP_OVERSIZE_SHEET_GUARD_ENABLED, design
// DESIGN-OVERSIZE-SHEET-GUARD-2026-09-24): the pure decisions, kept out of
// the cart handler, the dialog and the API client so they can be unit-tested
// without react-query, the router or the toasts.
//
// Rule (the API's OversizeSheetPolicy, mirrored): ONLY an ImageLogo counts -
// name/number/text logos carry no physical size in widthMm/heightMm. A logo
// is oversize when its width OR its height is above the API's limit in
// whole millimetres (default 300: 30.0 cm passes, 30.1 cm = 301 mm is
// refused). A missing, NaN or 0 side is "not evaluable" = not oversize (a
// partial list row, an upload still in flight) - the server is the
// backstop at CommitOrder/PlaceOrder.
//
// For a design ONLY designLogo.logo.widthMm/heightMm (the placed logo's own
// LogoDto) is read. DesignLogo.widthMm is the designer's display width
// (DesignerWidthMm ?? WidthMm on the API) and must never be used here.

export { SHEET_SIZE_UNAVAILABLE_ERROR_CODE };

export const DEFAULT_OVERSIZE_SHEET_MAX_MM = 300;
export const DEFAULT_OVERSIZE_SHEET_REQUEST_TTL_HOURS = 24;
/** At most this many "Request new sheet size" calls from one dialog. */
export const OVERSIZE_SHEET_MAX_REQUESTS = 10;
/** Client-side abort for the one request call. */
export const OVERSIZE_SHEET_REQUEST_TIMEOUT_MS = 8_000;
/**
 * sessionStorage key PREFIX of the per-viewer "Request sent" memory; the
 * full key is requestedStorageKey(scope), scoped per viewer (user + org).
 */
export const OVERSIZE_SHEET_REQUESTED_STORAGE_KEY = "oversizeSheet.requested";

export type OversizeSheetContext = "add" | "reorder" | "place";

/**
 * What an add-to-cart handler did under the guard: `refused` = logos (or
 * designs) the guard kept out of the cart, `added` = the rest, handed to
 * the cart as before. Flag off (or guard inactive) = refused is always 0,
 * so a caller's success toast fires exactly as today.
 */
export interface OversizeAddOutcome {
  added: number;
  refused: number;
}

export interface OversizeLogoInfo {
  logoId: number;
  serial: string;
  title: string;
  widthMm: number;
  heightMm: number;
  /** getLogoOrgId of the logo (0 = global logo, resolved at request time). */
  orgId: number;
  designId?: number;
}

export type SheetSizeRequestOutcome =
  | "sent"
  | "alreadyRequested"
  | "dailyCapReached"
  | "notConfigured"
  | "notApplicable"
  | "requestFailed";

function mmOf(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Number(x) > maxMm on either side (bothSides: on BOTH sides - the API's
 * OversizeLogoSheetBothSidesEnabled, carried in the hint); NaN / undefined /
 * 0 = false.
 */
export function isOversizeMm(
  widthMm: unknown,
  heightMm: unknown,
  maxMm: number,
  bothSides = false
): boolean {
  const w = mmOf(widthMm);
  const h = mmOf(heightMm);
  if (w === 0 || h === 0) return false;
  const limit = Number.isFinite(maxMm) && maxMm > 0 ? maxMm : DEFAULT_OVERSIZE_SHEET_MAX_MM;
  return bothSides ? w > limit && h > limit : w > limit || h > limit;
}

/** ONLY LogoType.ImageLogo is judged; every other type is never oversize. */
export function isOversizeLogo(
  logo: Pick<BaseLogo, "logoType" | "widthMm" | "heightMm"> | null | undefined,
  maxMm: number,
  bothSides = false
): boolean {
  if (!logo || logo.logoType !== LogoType.ImageLogo) return false;
  return isOversizeMm(logo.widthMm, logo.heightMm, maxMm, bothSides);
}

function toInfo(logo: BaseLogo, designId?: number): OversizeLogoInfo {
  return {
    logoId: logo.id,
    serial: logo.productSku ?? logo.serial ?? "",
    title: logo.title ?? "",
    widthMm: mmOf(logo.widthMm),
    heightMm: mmOf(logo.heightMm),
    orgId: getLogoOrgId(logo),
    designId,
  };
}

/** The oversize image logos among `logos`, deduped on logo id, first wins. */
export function collectOversizeFromLogos(
  logos: readonly (BaseLogo | null | undefined)[],
  maxMm: number,
  bothSides = false
): OversizeLogoInfo[] {
  const seen = new Set<number>();
  const out: OversizeLogoInfo[] = [];
  logos.forEach((logo) => {
    if (!logo || !(logo.id > 0) || seen.has(logo.id)) return;
    if (!isOversizeLogo(logo, maxMm, bothSides)) return;
    seen.add(logo.id);
    out.push(toInfo(logo));
  });
  return out;
}

/**
 * The oversize image logos of the STANDALONE logo lines among `items` (a
 * cart line of type "Logo": its baseLogo), deduped on logo id. Design lines
 * are never judged here (Nichlas 28/9: «Design skal ikke påvirkes af
 * dette»). Structural type: the cart's CartItem without importing the
 * presentation layer.
 */
export function collectOversizeFromCartLogoLines(
  items: readonly ({ type?: unknown; baseLogo?: BaseLogo | null } | null | undefined)[],
  maxMm: number,
  bothSides = false
): OversizeLogoInfo[] {
  return collectOversizeFromLogos(
    (items ?? []).map((item) => (item && item.type === "Logo" ? item.baseLogo : null)),
    maxMm,
    bothSides
  );
}

/**
 * The oversize image logos placed in `designs`: designLogo.logo.widthMm /
 * heightMm ONLY (never DesignLogo.widthMm), deduped on logo id.
 */
export function collectOversizeFromDesigns(
  designs: readonly (Design | null | undefined)[],
  maxMm: number,
  bothSides = false
): OversizeLogoInfo[] {
  const seen = new Set<number>();
  const out: OversizeLogoInfo[] = [];
  designs.forEach((design) => {
    design?.designLogos?.forEach((dl) => {
      const logo = dl?.logo;
      if (!logo || !(logo.id > 0) || seen.has(logo.id)) return;
      if (!isOversizeLogo(logo, maxMm, bothSides)) return;
      seen.add(logo.id);
      out.push(toInfo(logo, design.id));
    });
  });
  return out;
}

/**
 * Whether the guard applies to the current session: the API says the
 * caller's own org chain is listed, or an admin acts as a listed vendor.
 */
export function resolveOversizeApplies(
  hint: Pick<OversizeSheetGuardHint, "applies" | "vendorIds"> | null | undefined,
  isAdmin: boolean,
  actAsOrgId: number | undefined | null
): boolean {
  if (!hint) return false;
  if (hint.applies === true) return true;
  if (actAsOrgId == null || !(actAsOrgId > 0)) return false;
  // Flag oversizeSheetActAsGuardEnabled: under act-as the profile role is the
  // impersonated vendor's, never Admin - the act-as org alone decides (the
  // API sends vendorIds to admins only, and act-as state is admin-only).
  if (!isAdmin && !enviroment.oversizeSheetActAsGuardEnabled) return false;
  return Array.isArray(hint.vendorIds) && hint.vendorIds.includes(actAsOrgId);
}

/**
 * Whether the guard is live for this session: FE flag + API hint enabled +
 * applies (own chain or act-as). Flag off = always false.
 */
export function isOversizeSheetGuardActive(
  hint: OversizeSheetGuardHint | null | undefined,
  role: Role | undefined,
  actAsOrgId: number | undefined | null
): boolean {
  if (!enviroment.oversizeSheetGuardEnabled) return false;
  if (!hint || hint.enabled !== true) return false;
  return resolveOversizeApplies(hint, role === Role.Admin, actAsOrgId);
}

function mentions(value: unknown, marker: string): boolean {
  return (
    typeof value === "string" && value.toLowerCase().includes(marker.toLowerCase())
  );
}

/**
 * The API's 409 sheetSizeUnavailable at CommitOrder/PlaceOrder (marker kept
 * as errorCode by errorStatusFromBody, or named in the message when the
 * envelope carried none). Flag off = always false, whatever the error.
 */
export function isSheetSizeUnavailableError(error: unknown): boolean {
  if (!enviroment.oversizeSheetGuardEnabled) return false;
  if (!(error instanceof DataError)) return false;
  const status = error.errorStatus;
  if (!status || status.code !== 409) return false;
  return (
    mentions(status.errorCode, SHEET_SIZE_UNAVAILABLE_ERROR_CODE) ||
    mentions(status.message, SHEET_SIZE_UNAVAILABLE_ERROR_CODE) ||
    mentions(error.message, SHEET_SIZE_UNAVAILABLE_ERROR_CODE)
  );
}

/** The logos the 409 carried, as dialog rows (orgId 0 = resolve at request). */
export function sheetSizeUnavailableLogos(error: unknown): OversizeLogoInfo[] {
  const status = error instanceof DataError ? error.errorStatus : undefined;
  const logos: SheetSizeUnavailableLogo[] = status?.logos ?? [];
  const seen = new Set<number>();
  return logos
    .filter((l) => l.logoId > 0 && !seen.has(l.logoId) && seen.add(l.logoId))
    .map((l) => ({
      logoId: l.logoId,
      serial: l.serial ?? "",
      title: l.title ?? "",
      widthMm: mmOf(l.widthMm),
      heightMm: mmOf(l.heightMm),
      orgId: 0,
    }));
}

/** canRequest of the 409; absent = true (the hint decides the button). */
export function sheetSizeUnavailableCanRequest(error: unknown): boolean {
  const status = error instanceof DataError ? error.errorStatus : undefined;
  return status?.canRequest !== false;
}

function positiveOrZero(value: number | undefined | null): number {
  return value != null && Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * The organization the request is posted under. Never organizations/0;
 * 0 = nothing to post under.
 *
 * Not an admin: ALWAYS the session's org (the act-as org, else the
 * profile's org) - the org every logo on the page was fetched under, and
 * the one the caller has access to. The logo's own org (getLogoOrgId =
 * customer, else main organization) is NOT used: a branch customer
 * (Customer -> Customer -> Vendor) on a vendor-shared logo would post under
 * the VENDOR's org, which the API refuses with 400 "User does not have
 * access" (a red toast, the request can never be sent). The API's route
 * scoping (OversizeSheetRequestProbe.IsScopedTo) accepts any org the logo
 * is linked to, its main org or a global logo, so the session's org passes
 * for (nearly) every logo the session can see - the exceptions answer 404
 * and get ONE retry under the logo's org, see
 * resolveSheetSizeRequestRetryOrgId. The logo's org is otherwise only the
 * last resort when the session carries no org at all.
 *
 * Admin WITHOUT act-as (role Admin = the plain /profile): the logo's own
 * org (the admin has access to every org, and the mail then names the
 * logo's owner), a global logo (0) falling back to the profile's org. An
 * admin acting as an org is NOT on this branch: ProfileContext then reads
 * /profile/{orgId}, whose role is the act-as org's type (Vendor/Customer),
 * so the session rule applies and the act-as org is posted under - the
 * right vendor/customer attribution in the Zendesk mail, and auth always
 * passes for an admin.
 */
export function resolveSheetSizeRequestOrgId(
  logoOrgId: number,
  actAsOrgId: number | undefined | null,
  profileOrgId: number | undefined | null,
  isAdmin: boolean = false
): number {
  const logoOrg = positiveOrZero(logoOrgId);
  const sessionOrg = positiveOrZero(actAsOrgId) || positiveOrZero(profileOrgId);
  if (isAdmin) return logoOrg || sessionOrg;
  return sessionOrg || logoOrg;
}

/**
 * The org of the ONE retry after a 404 under `postedOrgId`, 0 = no retry.
 *
 * The API answers 404 both when its flag is off and when the logo is not
 * linked to the org posted under (OversizeSheetRequestProbe.IsScopedTo:
 * linked org, main org or global). A session can see logos that fail that
 * for its own org: a logo shared through a sharing relation (the giver is
 * linked, not the receiver), a parent customer on a logo linked only to
 * its child, or a design's fallback logo. For those the logo's own org is
 * what the probe accepts, and the session's access to it is what let the
 * logo be listed - so a non-admin retries once under the logo's org when
 * it is a different, positive org. An admin never retries (it already
 * posted under the logo's org, or the logo is global). Error path only:
 * a 404 costs at most one extra call.
 */
export function resolveSheetSizeRequestRetryOrgId(
  postedOrgId: number,
  logoOrgId: number,
  isAdmin: boolean = false
): number {
  if (isAdmin) return 0;
  const logoOrg = positiveOrZero(logoOrgId);
  return logoOrg > 0 && logoOrg !== positiveOrZero(postedOrgId) ? logoOrg : 0;
}

/** The request call's error was the API's 404 (flag off, or logo not linked to the org). */
export function isSheetSizeRequestNotFound(error: unknown): boolean {
  return error instanceof DataError && error.errorStatus?.code === 404;
}

/** Millimetres as centimetres, at most one decimal, in the UI's locale. */
export function formatCm(mm: number, locale?: string): string {
  const value = Number.isFinite(mm) ? mm / 10 : 0;
  try {
    return value.toLocaleString(locale || undefined, { maximumFractionDigits: 1 });
  } catch {
    return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
}

/**
 * Sorts the 202 body / the DataError of the request call into the outcome
 * that picks the toast: 202 sent / alreadyRequested / dailyCapReached from
 * the body; 503 notConfigured and 404 (API flag off - or, after the one
 * retry of resolveSheetSizeRequestRetryOrgId, a logo linked to neither
 * org) = notConfigured; 409 = notApplicable; every other error (503
 * sendFailed, network, abort) = requestFailed (the button stays active).
 */
export function classifySheetSizeRequestOutcome(
  result: { sent?: unknown; reason?: unknown } | null | undefined,
  error: unknown
): SheetSizeRequestOutcome {
  if (error === undefined && result) {
    if (result.sent === true) return "sent";
    if (result.reason === "alreadyRequested") return "alreadyRequested";
    if (result.reason === "dailyCapReached") return "dailyCapReached";
    return "requestFailed";
  }
  const status = error instanceof DataError ? error.errorStatus : undefined;
  if (!status) return "requestFailed";
  if (status.code === 404) return "notConfigured";
  if (status.code === 409) return "notApplicable";
  if (status.code === 503) {
    return mentions(status.errorCode, "notConfigured") ||
      mentions(status.message, "notConfigured")
      ? "notConfigured"
      : "requestFailed";
  }
  return "requestFailed";
}

// --- "Request sent" memory (per-viewer convenience in sessionStorage) ---
//
// Scoped per viewer: user (B2C id) + the org the session acts under (act-as
// org, else the profile's org). sessionStorage outlives logout/login and an
// act-as switch in the same tab, and the API's mail names the requesting
// user and org, so another user or another act-as org must not inherit a
// "Request sent" they never made - the API's per-logo window stays the
// backstop (a repeat answers 202 alreadyRequested, shown as an info toast).

export type RequestedMap = Record<number, string>;

/** The viewer the memory belongs to: `<userKey>:<orgId>` ("anon:0" when unknown). */
export function requestedScopeKey(
  userKey: string | undefined | null,
  orgId: number | undefined | null
): string {
  const user = typeof userKey === "string" && userKey.trim() !== "" ? userKey.trim() : "anon";
  const org = orgId != null && Number.isFinite(orgId) && orgId > 0 ? orgId : 0;
  return `${user}:${org}`;
}

/** The sessionStorage key of one viewer's memory. */
export function requestedStorageKey(scope: string): string {
  return `${OVERSIZE_SHEET_REQUESTED_STORAGE_KEY}:${scope || requestedScopeKey(undefined, undefined)}`;
}

function nowIso(now?: Date): string {
  return (now ?? new Date()).toISOString();
}

/** Drops entries older than ttlHours (or unparsable). */
export function pruneRequested(
  map: RequestedMap | null | undefined,
  ttlHours: number,
  now: Date = new Date()
): RequestedMap {
  const out: RequestedMap = {};
  if (!map || typeof map !== "object") return out;
  const ttlMs =
    (Number.isFinite(ttlHours) && ttlHours > 0
      ? ttlHours
      : DEFAULT_OVERSIZE_SHEET_REQUEST_TTL_HOURS) * 3_600_000;
  Object.entries(map).forEach(([key, iso]) => {
    const logoId = Number(key);
    if (!(logoId > 0) || typeof iso !== "string") return;
    const at = Date.parse(iso);
    if (!Number.isFinite(at)) return;
    if (now.getTime() - at > ttlMs) return;
    out[logoId] = iso;
  });
  return out;
}

/** Reads one viewer's memory; storage that throws or holds junk = empty. */
export function readRequested(
  scope: string,
  ttlHours: number,
  now: Date = new Date()
): RequestedMap {
  try {
    const raw = sessionStorage.getItem(requestedStorageKey(scope));
    if (!raw) return {};
    return pruneRequested(JSON.parse(raw) as RequestedMap, ttlHours, now);
  } catch {
    return {};
  }
}

/** Remembers one logo as requested for one viewer; storage that throws is ignored. */
export function writeRequested(
  scope: string,
  logoId: number,
  ttlHours: number,
  now: Date = new Date()
): RequestedMap {
  const next = { ...readRequested(scope, ttlHours, now), [logoId]: nowIso(now) };
  try {
    sessionStorage.setItem(requestedStorageKey(scope), JSON.stringify(next));
  } catch {
    // Private mode / quota / blocked storage: the in-memory copy still serves this page.
  }
  return next;
}
